import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { toast } from "sonner";
import { ExternalLink, ShoppingBag, ArrowLeft, ShieldCheck } from "lucide-react";

export default function MarketplaceProduct() {
  const { productId } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/marketplace/products/${productId}`);
        setProduct(data);
      } catch {
        toast.error("Product not found.");
        nav("/marketplace");
      } finally {
        setLoading(false);
      }
    })();
  }, [productId, nav]);

  if (loading) return <div className="bg-ink-900 min-h-screen py-24 text-center text-white/40">Loading…</div>;
  if (!product) return null;

  const img = (product.images || [])[0];

  const isExternal = !!product.external_url;

  const handleBuy = () => {
    if (isExternal) {
      window.open(product.external_url, "_blank");
      return;
    }
    if (product.sizes?.length > 0 && !selectedSize) {
      toast.error("Please select a size.");
      return;
    }
    setShowCheckout(true);
  };

  return (
    <div className="bg-ink-900 min-h-screen text-white" data-testid="product-detail">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10">
        <Link to="/marketplace" className="inline-flex items-center gap-2 text-white/60 hover:text-pfc-gold text-xs uppercase tracking-[0.3em] font-sub mb-8" data-testid="back-to-shop">
          <ArrowLeft size={14} /> Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Image */}
          <div className="bg-white/5 aspect-square overflow-hidden">
            {img ? (
              <img src={img} alt={product.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-white/30">
                <ShoppingBag size={64} />
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <Link to={`/marketplace/vendor/${product.vendor_slug}`} className="text-pfc-gold text-[11px] tracking-[0.3em] uppercase font-sub hover:underline" data-testid="product-vendor-link">
              {product.vendor_name}
              {isExternal && <span className="ml-2 text-white/40 inline-flex items-center gap-1"><ExternalLink size={10} /> PAOM</span>}
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl mt-3 leading-tight">{product.title}</h1>
            <div className="text-pfc-gold font-display text-3xl mt-4">${product.price.toFixed(2)}</div>

            <p className="text-white/70 mt-6 leading-relaxed text-base whitespace-pre-line">{product.description}</p>

            {product.sizes?.length > 0 && (
              <div className="mt-8">
                <div className="text-xs uppercase tracking-[0.3em] font-sub text-white/60 mb-3">Size</div>
                <div className="flex flex-wrap gap-2" data-testid="size-picker">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSelectedSize(s)}
                      data-testid={`size-${s}`}
                      className={`px-4 py-2 border text-sm font-sub transition ${
                        selectedSize === s
                          ? "bg-pfc-gold text-ink-900 border-pfc-gold"
                          : "border-white/20 text-white/80 hover:border-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isExternal && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.3em] font-sub text-white/60 mb-3">Quantity</div>
                <div className="flex items-center gap-3" data-testid="qty-picker">
                  <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="px-3 py-2 border border-white/20 hover:border-white">−</button>
                  <span className="text-lg w-8 text-center">{quantity}</span>
                  <button onClick={() => setQuantity((q) => Math.min(10, q + 1))} className="px-3 py-2 border border-white/20 hover:border-white">+</button>
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-col gap-3">
              <button
                onClick={handleBuy}
                className="w-full px-6 py-4 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition flex items-center justify-center gap-2"
                data-testid={isExternal ? "buy-external-btn" : "buy-now-btn"}
              >
                {isExternal ? (
                  <>
                    <ExternalLink size={16} /> Buy on PAOM
                  </>
                ) : (
                  <>
                    <ShoppingBag size={16} /> Buy Now · ${(product.price * quantity).toFixed(2)}
                  </>
                )}
              </button>
              {isExternal && (
                <p className="text-xs text-white/50 leading-relaxed">
                  Official PFC merch ships from <a href="https://paom.com" target="_blank" rel="noreferrer" className="text-pfc-gold underline">PAOM</a> (Print All Over Me). You'll be taken to their checkout to complete the order.
                </p>
              )}
            </div>

            <div className="mt-10 pt-8 border-t border-white/10 flex items-center gap-3 text-white/60 text-xs">
              <ShieldCheck size={14} className="text-pfc-gold" />
              Secure Stripe checkout · vendor ships directly · email confirmation sent.
            </div>
          </div>
        </div>
      </div>

      {showCheckout && (
        <CheckoutModal
          product={product}
          quantity={quantity}
          size={selectedSize}
          onClose={() => setShowCheckout(false)}
        />
      )}
    </div>
  );
}

function CheckoutModal({ product, quantity, size, onClose }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/marketplace/checkout", {
        product_id: product.product_id,
        quantity,
        size: size || null,
        origin_url: window.location.origin,
        ...form,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Failed to start checkout.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Checkout failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="checkout-modal">
      <div className="bg-ink-900 border border-pfc-gold/40 max-w-lg w-full p-8">
        <div className="flex items-baseline justify-between mb-6">
          <h3 className="font-display text-2xl text-white">Shipping Details</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm" data-testid="close-checkout">Close</button>
        </div>
        <div className="bg-white/5 px-4 py-3 mb-6 text-sm">
          <div className="text-white">{product.title} × {quantity}{size ? ` · ${size}` : ""}</div>
          <div className="text-pfc-gold mt-1">Total: ${(product.price * quantity).toFixed(2)}</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Input label="Full Name" required value={form.full_name} onChange={(v) => update("full_name", v)} testId="ship-name" />
          <Input label="Email" required type="email" value={form.email} onChange={(v) => update("email", v)} testId="ship-email" />
          <Input label="Phone" value={form.phone} onChange={(v) => update("phone", v)} testId="ship-phone" />
          <Input label="Address line 1" required value={form.address_line1} onChange={(v) => update("address_line1", v)} testId="ship-addr1" />
          <Input label="Address line 2" value={form.address_line2} onChange={(v) => update("address_line2", v)} testId="ship-addr2" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="City" required value={form.city} onChange={(v) => update("city", v)} testId="ship-city" />
            <Input label="State / Region" required value={form.state} onChange={(v) => update("state", v)} testId="ship-state" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Postal Code" required value={form.postal_code} onChange={(v) => update("postal_code", v)} testId="ship-zip" />
            <Input label="Country" required value={form.country} onChange={(v) => update("country", v)} testId="ship-country" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="confirm-pay-btn"
            className="w-full py-3 mt-4 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-60"
          >
            {loading ? "Redirecting…" : `Pay $${(product.price * quantity).toFixed(2)} →`}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, required, type = "text", testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-pfc-gold focus:outline-none"
      />
    </label>
  );
}
